import React, { useState, useEffect, useCallback } from 'react';
import { 
  Box, 
  Container, 
  Typography, 
  Button, 
  Card, 
  CardContent, 
  Alert,
  CircularProgress,
  Paper,
  Divider,
  ToggleButtonGroup,
  ToggleButton,
  TextField,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton
} from '@mui/material';
import { CheckCircle, Payment, QrCode2, CreditCard, Close } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { createBilling, CreateBillingResponse, getPlans, Plan } from '../services/subscription';
import { prepararCheckout } from '../services/mercadopago';
import TaxIdField, { validateTaxId } from '../components/TaxIdField';
import MercadoPagoCheckout from '../components/MercadoPagoCheckout';
import PlanCard from '../components/PlanCard';

const Pagamento: React.FC = () => {
  const navigate = useNavigate();
  const { user, subscription } = useAuth();
  const [loading, setLoading] = useState(false);
  const [loadingPlan, setLoadingPlan] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [plans, setPlans] = useState<Plan[]>([]);
  const [selectedPlan, setSelectedPlan] = useState<Plan | null>(null);
  const [paymentMethod, setPaymentMethod] = useState<'PIX' | 'CREDIT_CARD'>('PIX');
  const [customerTaxId, setCustomerTaxId] = useState('');
  const [customerCellphone, setCustomerCellphone] = useState('');
  const [checkoutUrl, setCheckoutUrl] = useState<string | null>(null);
  const [showCheckout, setShowCheckout] = useState(false);
  const [mercadoPagoPublicKey, setMercadoPagoPublicKey] = useState<string | null>(null);
  const [mercadoPagoPlanAmount, setMercadoPagoPlanAmount] = useState<number>(0);
  const [showCreditCardForm, setShowCreditCardForm] = useState(false);
  const [taxIdError, setTaxIdError] = useState<string>('');
  const [cellphoneError, setCellphoneError] = useState<string>('');
  const [hasActivePayment, setHasActivePayment] = useState<boolean | null>(null);
  const [pollingIntervalId, setPollingIntervalId] = useState<NodeJS.Timeout | null>(null);

  // Preencher campos com dados do usuário se já existirem
  useEffect(() => {
    if (user?.taxId) {
      setCustomerTaxId(user.taxId);
    }
    if (user?.cellphone) {
      setCustomerCellphone(user.cellphone);
    }
  }, [user]);

  useEffect(() => {
    const query = new URLSearchParams(globalThis.location.search);

    if (query.get('success')) {
      setSuccess(true);
      setTimeout(() => {
        navigate('/tw/dashboard');
      }, 3000);
    }
  }, [navigate]);

  // Função centralizada para tratar sucesso do pagamento
  const handlePaymentSuccess = useCallback(() => {
    // Limpar polling se estiver ativo
    if (pollingIntervalId) {
      clearInterval(pollingIntervalId);
      setPollingIntervalId(null);
    }
    
    setShowCheckout(false);
    setSuccess(true);
    setTimeout(() => {
      navigate('/tw/dashboard');
    }, 3000);
  }, [pollingIntervalId, navigate]);

  // Função para verificar status do pagamento no backend
  const checkPaymentStatus = useCallback(async () => {
    try {
      // Verificar se o contexto de subscription foi atualizado
      // O subscription é atualizado automaticamente quando o pagamento é processado
      if (subscription?.hasActiveAccess) {
        handlePaymentSuccess();
        return true;
      }
    } catch (error) {
      console.error('Erro ao verificar status do pagamento:', error);
    }
    return false;
  }, [subscription, handlePaymentSuccess]);

  // Listener para mensagens do iframe e polling para verificar pagamento
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      // Verificar diferentes origens possíveis do AbacatePay
      const allowedOrigins = [
        'https://abacatepay.com',
        'https://www.abacatepay.com',
        'https://checkout.abacatepay.com'
      ];
      
      if (!allowedOrigins.includes(event.origin)) {
        return;
      }
      
      // Verificar diferentes tipos de eventos de sucesso
      const data = event.data;
      const isPaymentSuccess = 
        data?.type === 'payment_success' ||
        data?.status === 'paid' ||
        data?.status === 'approved' ||
        data?.event === 'payment_completed' ||
        data?.payment?.status === 'paid';
        
      if (isPaymentSuccess) {
        handlePaymentSuccess();
      }
    };

    globalThis.addEventListener('message', handleMessage);
    
    // Iniciar polling apenas quando o checkout estiver aberto
    let intervalId: NodeJS.Timeout | null = null;
    if (showCheckout) {
      intervalId = setInterval(async () => {
        const paymentCompleted = await checkPaymentStatus();
        if (paymentCompleted) {
          clearInterval(intervalId!);
        }
      }, 5000); // Verificar a cada 5 segundos
      
      setPollingIntervalId(intervalId);
      
      // Timeout de segurança: após 15 minutos, fechar automaticamente
      const timeoutId = setTimeout(() => {
        setShowCheckout(false);
        if (intervalId) {
          clearInterval(intervalId);
        }
      }, 15 * 60 * 1000); // 15 minutos
      
      return () => {
        globalThis.removeEventListener('message', handleMessage);
        if (intervalId) {
          clearInterval(intervalId);
        }
        clearTimeout(timeoutId);
      };
    }
    
    return () => {
      globalThis.removeEventListener('message', handleMessage);
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [handlePaymentSuccess, checkPaymentStatus, showCheckout]);

  useEffect(() => {
    // Buscar todos os planos disponíveis do backend
    const fetchPlans = async () => {
      try {
        setLoadingPlan(true);
        
        // Verificar se o usuário já tem assinatura ativa
        if (subscription?.hasActiveAccess) {
          setHasActivePayment(true);
          setLoadingPlan(false);
          return;
        }
        
        setHasActivePayment(false);
        const plansData = await getPlans();
        setPlans(plansData);
        
        // Selecionar plano do meio (Profissional) como padrão
        if (plansData.length > 0) {
          const defaultPlanIndex = Math.floor(plansData.length / 2);
          setSelectedPlan(plansData[defaultPlanIndex]);
        }
      } catch (err: any) {
        console.error('Erro ao buscar planos:', err);
        setError('Erro ao carregar informações dos planos. Tente novamente.');
      } finally {
        setLoadingPlan(false);
      }
    };

    fetchPlans();
  }, [subscription]);

  // Effect para detectar quando a subscription é atualizada (pagamento processado)
  useEffect(() => {
    if (showCheckout && subscription?.hasActiveAccess) {
      handlePaymentSuccess();
    }
  }, [showCheckout, subscription?.hasActiveAccess, handlePaymentSuccess]);

  const handleValidateTaxId = (value: string): boolean => {
    const validation = validateTaxId(value, true);
    
    if (!validation.isValid && validation.error) {
      setTaxIdError(validation.error);
      return false;
    }
    
    setTaxIdError('');
    return true;
  };

  const validateCellphone = (value: string): boolean => {
    if (!value) {
      // Telefone é opcional
      setCellphoneError('');
      return true;
    }
    
    const cleanValue = value.replaceAll(/\D/g, '');
    
    // Deve ter pelo menos 12 dígitos (55 + DDD + número com 8 ou 9 dígitos)
    if (cleanValue.length < 12 || cleanValue.length > 13) {
      setCellphoneError('Telefone inválido. Use o formato: +55 (DDD) Número');
      return false;
    }
    
    // Verificar se começa com 55 (código do Brasil)
    if (!cleanValue.startsWith('55')) {
      setCellphoneError('Telefone deve começar com +55 (Brasil)');
      return false;
    }
    
    setCellphoneError('');
    return true;
  };

  const handleCreateBilling = async () => {
    if (!user?.email || !user?.name) {
      setError('Dados do usuário não encontrados. Faça login novamente.');
      return;
    }

    if (!selectedPlan) {
      setError('Selecione um plano para continuar.');
      return;
    }

    // Validar campos
    const isTaxIdValid = handleValidateTaxId(customerTaxId);
    const isCellphoneValid = validateCellphone(customerCellphone);
    
    if (!isTaxIdValid) {
      document.getElementById('tax-id-field')?.focus();
      return;
    }
    
    if (!isCellphoneValid) {
      document.getElementById('cellphone-field')?.focus();
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Tratamento específico baseado no método de pagamento
      if (paymentMethod === 'PIX') {
        // PIX: usar AbacatePay com checkout inline
        const requestData = {
          planId: selectedPlan.id,
          customerEmail: user.email,
          customerName: user.name,
          customerTaxId: customerTaxId,
          customerCellphone: customerCellphone || undefined,
          paymentMethod: paymentMethod
        };

        const response: CreateBillingResponse = await createBilling(requestData);

        if (response.error) {
          throw new Error(response.error);
        }

        if (response.billing?.url) {
          setCheckoutUrl(response.billing.url);
          setShowCheckout(true);
        } else {
          throw new Error('URL de pagamento PIX não encontrada');
        }
      } else {
        // Cartão de Crédito: usar Mercado Pago com checkout transparente
        const requestData = {
          customerEmail: user.email,
          customerName: user.name,
          customerTaxId: customerTaxId,
          planId: selectedPlan.id
        };

        const response = await prepararCheckout(requestData);

        if (response.errorMessage) {
          throw new Error(response.errorMessage);
        }

        if (response.publicKey && response.planAmount) {
          setMercadoPagoPublicKey(response.publicKey);
          setMercadoPagoPlanAmount(response.planAmount);
          setShowCreditCardForm(true);
        } else {
          throw new Error('Erro ao preparar checkout');
        }
      }
    } catch (err: any) {
      setError(err.message || 'Erro ao processar o pagamento. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <Container maxWidth="md" sx={{ mt: 8, mb: 8 }}>
        <Card 
          elevation={3}
          sx={{ 
            borderRadius: 4,
            textAlign: 'center',
            p: 4
          }}
        >
          <CardContent>
            <CheckCircle 
              sx={{ 
                fontSize: 80, 
                color: 'success.main',
                mb: 2
              }} 
            />
            <Typography variant="h4" gutterBottom fontWeight="bold">
              Pagamento Confirmado!
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              Obrigado por assinar o ThinkWay! Agora você tem acesso completo
              a todas as funcionalidades da nossa plataforma.
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Redirecionando para o dashboard...
            </Typography>
          </CardContent>
        </Card>
      </Container>
    );
  }

  if (loadingPlan) {
    return (
      <Container maxWidth="md" sx={{ mt: 8, mb: 8 }}>
        <Box sx={{ textAlign: 'center' }}>
          <CircularProgress size={60} />
          <Typography variant="body1" sx={{ mt: 2 }}>
            Carregando informações do plano...
          </Typography>
        </Box>
      </Container>
    );
  }

  // Se o usuário já tem assinatura ativa, exibir mensagem de sucesso
  if (hasActivePayment) {
    return (
      <Container maxWidth="md" sx={{ mt: 8, mb: 8 }}>
        <Card 
          elevation={3}
          sx={{ 
            borderRadius: 4,
            textAlign: 'center',
            p: 4
          }}
        >
          <CardContent>
            <CheckCircle 
              sx={{ 
                fontSize: 80, 
                color: 'success.main',
                mb: 2
              }} 
            />
            <Typography variant="h4" gutterBottom fontWeight="bold">
              Está Tudo Certo!
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              Você já possui uma assinatura ativa do ThinkWay.
              Não há pendências de pagamento.
            </Typography>
            <Button 
              variant="contained" 
              size="large"
              onClick={() => navigate('/tw/dashboard')}
              sx={{ mt: 2 }}
            >
              Voltar para Dashboard
            </Button>
          </CardContent>
        </Card>
      </Container>
    );
  }

  if (!selectedPlan) {
    return (
      <Container maxWidth="md" sx={{ mt: 8, mb: 8 }}>
        <Alert severity="error">
          Não foi possível carregar as informações do plano. Tente novamente.
        </Alert>
      </Container>
    );
  }

  if (!selectedPlan) {
    return (
      <Container maxWidth="md" sx={{ mt: 4, mb: 8 }}>
        <Alert severity="info">
          Carregando planos disponíveis...
        </Alert>
      </Container>
    );
  }

  const formattedPrice = `R$ ${selectedPlan.totalPriceBRL.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  const formattedInstallmentPrice = `R$ ${selectedPlan.installmentPriceBRL.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;



  const formatPhone = (value: string) => {
    // Remove tudo que não é dígito
    const numbers = value.replaceAll(/\D/g, '');
    
    // Formata telefone: +55 (11) 99999-9999
    if (numbers.length <= 2) {
      return `+${numbers}`;
    } else if (numbers.length <= 4) {
      return `+${numbers.slice(0, 2)} (${numbers.slice(2)}`;
    } else if (numbers.length <= 9) {
      return `+${numbers.slice(0, 2)} (${numbers.slice(2, 4)}) ${numbers.slice(4)}`;
    } else {
      return `+${numbers.slice(0, 2)} (${numbers.slice(2, 4)}) ${numbers.slice(4, 9)}-${numbers.slice(9, 13)}`;
    }
  };

  const handleTaxIdChange = (value: string) => {
    setCustomerTaxId(value);
    // Limpar erro ao digitar
    if (taxIdError) {
      setTaxIdError('');
    }
  };

  const handleCellphoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatPhone(e.target.value);
    setCustomerCellphone(formatted);
    // Limpar erro ao digitar
    if (cellphoneError) {
      setCellphoneError('');
    }
  };

  const handlePaymentMethodChange = (_event: React.MouseEvent<HTMLElement>, newMethod: 'PIX' | 'CREDIT_CARD' | null) => {
    if (newMethod !== null) {
      setPaymentMethod(newMethod);
    }
  };

  const getButtonText = () => {
    if (loading) return 'Processando...';
    if (paymentMethod === 'PIX') return `Pagar ${formattedPrice} via PIX`;
    return `Pagar em até 12x de ${formattedInstallmentPrice}`;
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 8 }}>
      <Box sx={{ textAlign: 'center', mb: 4 }}>
        <Typography variant="h3" gutterBottom fontWeight="bold">
          Assine o ThinkWay
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Escolha o plano ideal para sua empresa
        </Typography>
      </Box>

      {error && (
        <Alert 
          severity="error" 
          sx={{ mb: 3 }}
          onClose={() => setError(null)}
        >
          {error}
        </Alert>
      )}

      {/* Plan Selection Cards */}
      {loadingPlan ? (
        <Box display="flex" justifyContent="center" py={8}>
          <CircularProgress size={60} />
        </Box>
      ) : (
        <>
          <Typography variant="h5" gutterBottom sx={{ mb: 3, textAlign: 'center' }}>
            Selecione seu Plano
          </Typography>
          
          <Box 
            sx={{ 
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: '1fr', md: 'repeat(3, 1fr)' },
              gap: 3,
              mb: 5
            }}
          >
            {plans.map((plan) => (
              <PlanCard
                key={plan.id}
                plan={plan}
                selected={selectedPlan?.id === plan.id}
                onSelect={() => setSelectedPlan(plan)}
              />
            ))}
          </Box>
        </>
      )}

      {selectedPlan && (
        <Paper elevation={3} sx={{ mt: 4, p: 4 }}>
          <Typography variant="h5" gutterBottom sx={{ mb: 3, textAlign: 'center' }}>
            Dados para pagamento
          </Typography>

        <Box sx={{ mb: 4, maxWidth: 500, mx: 'auto' }}>
          <TaxIdField
            id="tax-id-field"
            value={customerTaxId}
            onChange={handleTaxIdChange}
            onError={setTaxIdError}
            required
            error={!!taxIdError}
            readOnly={!!user?.taxId}
            helperText={taxIdError || (user?.taxId ? "Informado no cadastro" : "Informe seu CPF (11 dígitos) ou CNPJ (14 dígitos)")}
            sx={{ mb: 2 }}
          />

          <TextField
            id="cellphone-field"
            fullWidth
            label="Telefone Celular (opcional)"
            value={customerCellphone}
            onChange={handleCellphoneChange}
            placeholder="+55 (11) 99999-9999"
            error={!!cellphoneError}
            slotProps={{ 
              htmlInput: { 
                maxLength: 19,
                readOnly: !!user?.cellphone 
              } 
            }}
            helperText={cellphoneError || (user?.cellphone ? "Informado no cadastro" : "Formato: +55 (DDD) Número")}
          />
        </Box>

        <Divider sx={{ my: 3 }} />

        <Typography variant="h5" gutterBottom sx={{ mb: 3, textAlign: 'center' }}>
          Escolha a forma de pagamento
        </Typography>
        
        <ToggleButtonGroup
          value={paymentMethod}
          exclusive
          onChange={handlePaymentMethodChange}
          aria-label="payment method"
          sx={{ mb: 4, width: '100%', maxWidth: 500, mx: 'auto', display: 'flex' }}
        >
          <ToggleButton 
            value="PIX" 
            aria-label="pix"
            sx={{ 
              flex: 1,
              py: 3,
              flexDirection: 'column',
              gap: 1,
              '&.Mui-selected': {
                backgroundColor: 'primary.main',
                color: 'white',
                '&:hover': {
                  backgroundColor: 'primary.dark',
                }
              }
            }}
          >
            <QrCode2 sx={{ fontSize: 40 }} />
            <Typography variant="subtitle1" fontWeight="bold">PIX</Typography>
            <Typography variant="caption">Pagamento instantâneo</Typography>
          </ToggleButton>
          
          <ToggleButton 
            value="CREDIT_CARD" 
            aria-label="credit card"
            sx={{ 
              flex: 1,
              py: 3,
              flexDirection: 'column',
              gap: 1,
              '&.Mui-selected': {
                backgroundColor: 'primary.main',
                color: 'white',
                '&:hover': {
                  backgroundColor: 'primary.dark',
                }
              }
            }}
          >
            <CreditCard sx={{ fontSize: 40 }} />
            <Typography variant="subtitle1" fontWeight="bold">Cartão de Crédito</Typography>
            <Typography variant="caption">Parcele em até 12x</Typography>
          </ToggleButton>
        </ToggleButtonGroup>

        <Box sx={{ mb: 3, textAlign: 'center' }}>
          {paymentMethod === 'PIX' ? (
            <>
              <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                Pagamento via PIX
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 400, mx: 'auto' }}>
                Ao clicar em "Pagar", o checkout abrirá aqui mesmo no site.
                Você verá o QR Code para escanear e realizar o pagamento instantaneamente.
              </Typography>
            </>
          ) : (
            <>
              <CreditCard sx={{ fontSize: 60, color: 'primary.main' }} />
              <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                Pagamento com Cartão de Crédito
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 400, mx: 'auto' }}>
                Ao clicar em "Pagar", o checkout abrirá aqui mesmo no site.
                Você poderá parcelar em até 12x no cartão de crédito.
              </Typography>
            </>
          )}
        </Box>

        <Box sx={{ textAlign: 'center' }}>
          <Button
            variant="contained"
            size="large"
            disabled={loading}
            onClick={handleCreateBilling}
            startIcon={loading ? <CircularProgress size={20} /> : <Payment />}
            sx={{ 
              py: 2.5,
              fontSize: '1.2rem',
              maxWidth: 500,
              width: '100%',
              fontWeight: 'bold'
            }}
          >
            {getButtonText()}
          </Button>

          <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
            Pagamento seguro processado pelo {paymentMethod === 'PIX' ? 'AbacatePay 🥑' : 'Mercado Pago 💳'}
          </Typography>
        </Box>
      </Paper>
      )}

      {/* Modal com checkout inline do AbacatePay (PIX) */}
      <Dialog 
        open={showCheckout} 
        onClose={() => setShowCheckout(false)}
        maxWidth="md"
        fullWidth
        slotProps={{
          paper: {
            sx: {
              minHeight: '80vh',
              maxHeight: '90vh'
            }
          }
        }}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography variant="h6">
              Pagamento via PIX
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center' }}>
              ⚡ Após concluir o pagamento, esta janela fechará automaticamente
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center', mt: 0.5 }}>
              🔄 Verificando status do pagamento a cada 5 segundos...
            </Typography>
          </Box>
          <IconButton onClick={() => setShowCheckout(false)} edge="end">
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          {checkoutUrl && (
            <Box sx={{ position: 'relative', height: '100%', minHeight: '600px' }}>
              {/* Indicador de monitoramento */}
              <Box 
                sx={{ 
                  position: 'absolute', 
                  top: 8, 
                  right: 8, 
                  zIndex: 10,
                  backgroundColor: 'success.main',
                  color: 'white',
                  px: 1,
                  py: 0.5,
                  borderRadius: 1,
                  fontSize: '0.75rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5
                }}
              >
                <CircularProgress size={12} sx={{ color: 'white' }} />
                Monitorando pagamento
              </Box>
              
              <iframe
                src={checkoutUrl}
                style={{
                  width: '100%',
                  height: '100%',
                  minHeight: '600px',
                  border: 'none'
                }}
                title="Checkout AbacatePay"
              />
            </Box>
          )}
        </DialogContent>
      </Dialog>

      {/* Modal com formulário de cartão do Mercado Pago (Checkout Transparente) */}
      <Dialog 
        open={showCreditCardForm} 
        onClose={() => setShowCreditCardForm(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">
            Pagamento com Cartão de Crédito
          </Typography>
          <IconButton onClick={() => setShowCreditCardForm(false)} edge="end">
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {mercadoPagoPublicKey && mercadoPagoPlanAmount > 0 && selectedPlan && user && (
            <MercadoPagoCheckout
              publicKey={mercadoPagoPublicKey}
              planAmount={mercadoPagoPlanAmount}
              planName={selectedPlan.name}
              customerName={user.name}
              customerEmail={user.email}
              customerTaxId={customerTaxId}
              onPaymentSuccess={handlePaymentSuccess}
              onPaymentError={(error) => {
                // Erro já é exibido dentro do componente MercadoPagoCheckout
                // Não fechar a modal para permitir que o usuário veja o erro e tente novamente
                console.error('Erro no pagamento:', error);
              }}
            />
          )}
        </DialogContent>
      </Dialog>
    </Container>
  );
};

export default Pagamento;